import { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ConversationAttributeItem } from '@/entities/attributes';
import type { TimeIntervalFormat } from '@/shared/lib';
import { type AddOption, AddOptionPopover } from '@/shared/ui';
import { createEmptyPropertyValue, getPropertyAttribute, type RecordValueSetForm } from '../model/recordValueSet';
import { RecordPropertyCell } from './RecordPropertyCell';

type Props = {
  /** Свойства, которые ещё можно добавить в набор */
  options: AddOption[];
  /** Подписи свойств по их ключам */
  propertyLabels: ReadonlyMap<string, string>;
  /** Атрибуты набора по их путям */
  attributesByPath: ReadonlyMap<string, ConversationAttributeItem>;
  /** Действующий формат показа интервала времени — от него зависит поле длительности */
  timeDisplayFormat: TimeIntervalFormat;
  disabled: boolean;
};

/** Набор значений блока загрузки: ячейки заданных свойств и добавление свойства из перечня */
export const RecordValueSet: FC<Props> = ({
  options,
  propertyLabels,
  attributesByPath,
  timeDisplayFormat,
  disabled,
}) => {
  const { t } = useTranslation(['records']);
  const { control } = useFormContext<RecordValueSetForm>();
  const { fields, append, remove } = useFieldArray({ control, name: 'properties' });

  const handleAdd = (property: string) => {
    append({ property, value: createEmptyPropertyValue(property, attributesByPath, timeDisplayFormat) });
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      {fields.map((field, index) => (
        <RecordPropertyCell
          key={field.id}
          name={`properties.${index}.value`}
          property={field.property}
          label={propertyLabels.get(field.property) ?? field.property}
          attribute={getPropertyAttribute(field.property, attributesByPath)}
          timeDisplayFormat={timeDisplayFormat}
          disabled={disabled}
          onRemove={() => remove(index)}
        />
      ))}
      <div className="flex items-center gap-2">
        <AddOptionPopover
          isIconOnly
          label={t('records:valueSet.add')}
          options={options}
          searchPlaceholder={t('records:valueSet.search')}
          emptyMessage={t('records:valueSet.notFound')}
          onAdd={handleAdd}
          disabled={disabled}
        />
        {fields.length === 0 && <span className="text-sm text-muted-foreground">{t('records:valueSet.empty')}</span>}
      </div>
    </div>
  );
};
